import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ONBOARDING_BANNER_COLOR,
  ONBOARDING_BANNER_COLOR_VALUES,
  ONBOARDING_PLAN_STATUS,
  ONBOARDING_PLAN_STATUS_VALUES,
  OnboardingBannerColor,
  OnboardingPlanStatus,
} from "@forgepoint/shared";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import { parseEnumValue } from "@/lib/utils";

const STATUS_LABELS: Record<OnboardingPlanStatus, string> = {
  DRAFT: "Draft — hidden from the team",
  ACTIVE: "Active — tab and banner visible",
  COMPLETED: "Completed — onboarding finished",
};

const BANNER_COLOR_LABELS: Record<OnboardingBannerColor, string> = {
  WARNING: "Amber",
  DESTRUCTIVE: "Red",
  INFO: "Blue",
  PRIMARY: "Brand",
};

/**
 * Plan configuration: lifecycle status and the optional banner message
 * override. Schedule dates live in the go-live schedule section. Saving with
 * no existing plan creates it in DRAFT staging.
 */
export function PlanSettingsSection() {
  const plan = useOnboardingStore((state) => state.plan);
  const planSaveState = useOnboardingStore((state) => state.planSaveState);
  const savePlan = useOnboardingStore((state) => state.savePlan);

  const [status, setStatus] = useState<OnboardingPlanStatus>(
    ONBOARDING_PLAN_STATUS.DRAFT,
  );
  const [bannerColor, setBannerColor] = useState<OnboardingBannerColor>(
    ONBOARDING_BANNER_COLOR.WARNING,
  );
  const [bannerMessage, setBannerMessage] = useState("");

  useEffect(() => {
    if (plan) {
      setStatus(plan.status);
      setBannerColor(plan.bannerColor);
      setBannerMessage(plan.bannerMessage ?? "");
    }
  }, [plan]);

  const handleSave = () => {
    savePlan({
      status,
      bannerColor,
      bannerMessage: bannerMessage.trim() || null,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding plan</CardTitle>
        <CardDescription>
          {plan
            ? "Configure the plan's status and banner."
            : "No plan exists yet — save to create one in Draft."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs space-y-1.5">
          <Label htmlFor="onboarding-plan-status">Status</Label>
          <Select
            value={status}
            onValueChange={(value) => {
              const parsed = parseEnumValue(
                ONBOARDING_PLAN_STATUS_VALUES,
                value,
              );
              if (parsed) {
                setStatus(parsed);
              }
            }}
          >
            <SelectTrigger id="onboarding-plan-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ONBOARDING_PLAN_STATUS_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {STATUS_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="max-w-xs space-y-1.5">
          <Label htmlFor="onboarding-banner-color">Banner color</Label>
          <Select
            value={bannerColor}
            onValueChange={(value) => {
              const parsed = parseEnumValue(
                ONBOARDING_BANNER_COLOR_VALUES,
                value,
              );
              if (parsed) {
                setBannerColor(parsed);
              }
            }}
          >
            <SelectTrigger id="onboarding-banner-color">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ONBOARDING_BANNER_COLOR_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {BANNER_COLOR_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="onboarding-banner-message">
            Banner message override
          </Label>
          <Textarea
            id="onboarding-banner-message"
            placeholder="Leave empty for the default banner text"
            value={bannerMessage}
            onChange={(event) => setBannerMessage(event.target.value)}
            rows={2}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={planSaveState.isSaving}>
            Save
            {planSaveState.isSaving && (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            )}
          </Button>
          {planSaveState.error && (
            <span className="text-sm text-destructive">
              {planSaveState.error}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
